import { DataGrid } from "@/components/DataGrid";
import { type DmUsageResponse } from "@/features/accountSettings/api";
import { Box } from "@mui/material";
import {
  type GridColDef,
  type GridRenderCellParams,
} from "@mui/x-data-grid-pro";
import { format } from "date-fns";
import { useMemo } from "react";
import { formatNumberWithComma } from "@/utils/formatNumber";

interface DmUsageHeaders
  extends Omit<GridColDef, "field" | "headerName" | "width"> {
  field: keyof DmUsageRow;
  headerName: string;
  width: number;
}

interface DmUsageRow {
  id: number;
  date: string;
  memo: string;
  quantity: number;
  price: number;
  amount: number;
}

interface Props {
  dmUsages: DmUsageResponse[];
}

const column: DmUsageHeaders[] = [
  {
    field: "date",
    headerName: "利用日",
    width: 180,
  },
  {
    field: "memo",
    headerName: "内容",
    width: 320,
    // 管理画面で入力した費目は改行を含む場合があるため、折り返して表示する
    renderCell: ({ value }: GridRenderCellParams<string, DmUsageRow>) => (
      <Box sx={{ whiteSpace: "pre-line" }}>{value}</Box>
    ),
  },
  {
    field: "quantity",
    headerName: "数量",
    width: 120,
    renderCell: ({ value }: GridRenderCellParams<number, DmUsageRow>) =>
      formatNumberWithComma(value),
  },
  {
    field: "price",
    headerName: "単価",
    width: 120,
    renderCell: ({ value }: GridRenderCellParams<number, DmUsageRow>) =>
      formatNumberWithComma(value),
  },
  {
    field: "amount",
    headerName: "金額",
    width: 120,
    renderCell: ({ value }: GridRenderCellParams<number, DmUsageRow>) =>
      formatNumberWithComma(value),
  },
];

export const DmUsageGrid: React.FC<Props> = (props) => {
  const { dmUsages } = props;
  const rows = useMemo(
    () =>
      dmUsages.map<DmUsageRow>((dmUsage) => {
        return {
          id: dmUsage.id,
          date: format(new Date(dmUsage.createdAt), "yyyy/MM/dd"),
          memo: dmUsage.memo,
          quantity: dmUsage.quantity,
          price: dmUsage.price,
          amount: dmUsage.amount,
        };
      }),
    [dmUsages]
  );
  return (
    <DataGrid
      rows={rows}
      columns={column}
      getRowHeight={() => "auto"}
      sx={{ "& .MuiDataGrid-cell": { py: 1 } }}
    ></DataGrid>
  );
};
